import { useMemo } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentType,
  ComplianceProgramStep,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";

import { getReviewQuestionScope } from "../review-questions/review-question-rows";
import { ProgramForm, ProgramFormStep } from "../types";
import { toQuestionSetOption } from "./question-set-option";
import QuestionSetSelector from "./question-set-selector";
import { QuestionList } from "./question-tab-section";
import RoleGoalValueQuestionRows from "./role-goal-value-question-rows";

const PeerAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  publishedReviewId,
  onAddOrUpdateStep,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  showQuestionSetError,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  publishedReviewId: number | null;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  showQuestionSetError: boolean;
}) => {
  const label = useLabel();

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(({ assessmentType }) => assessmentType === AssessmentType.Peer)
      .filter(
        (assessmentGroup) =>
          !assessmentGroup.isOnlySelfAssessment &&
          !assessmentGroup.hasNpsQuestions,
      )
      .map(toQuestionSetOption);
  }, [availableAssessmentGroups]);

  const peerReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.PeerReview,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return peerReviewStep && peerReviewStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === peerReviewStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, peerReviewStep]);

  return (
    peerReviewStep && (
      <RoleGoalValueQuestionRows
        step={peerReviewStep}
        canUpdate={canUpdate}
        onAddOrUpdateStep={onAddOrUpdateStep}
        proposedComplianceProgram={proposedComplianceProgram}
      >
        {(roleGoalValueItems) => (
          <QuestionList
            title={`Peer ${label("review", { capitalize: true })}`}
            items={[
              {
                key: "question-set",
                added: !!selectedAssessmentGroup,
                row: (
                  <QuestionSetSelector
                    review={getReviewQuestionScope(
                      publishedReviewId,
                      peerReviewStep,
                    )}
                    assessmentType={AssessmentType.Peer}
                    availableAssessmentGroupSelectOptions={
                      availableAssessmentGroupSelectOptions
                    }
                    canUpdate={canUpdate}
                    onUpdateAssessmentTemplate={(assessmentGroupId) => {
                      onAddOrUpdateStep({
                        ...peerReviewStep,
                        questionSetIds: assessmentGroupId
                          ? [assessmentGroupId]
                          : [],
                      });
                    }}
                    onAssessmentGroupCreated={onAssessmentGroupCreated}
                    onAssessmentGroupArchived={onAssessmentGroupArchived}
                    selectedAssessmentGroup={selectedAssessmentGroup}
                    error={
                      showQuestionSetError &&
                      peerReviewStep.questionSetIds.length === 0
                        ? "Please choose a question set"
                        : undefined
                    }
                  />
                ),
              },
              ...roleGoalValueItems,
            ]}
          />
        )}
      </RoleGoalValueQuestionRows>
    )
  );
};
export default PeerAssessmentStep;
